/**
 * JSON-backed access to `localStorage`, in two flavours.
 *
 * | Hook | Returns | Re-renders on change? | Use it for |
 * | --- | --- | --- | --- |
 * | {@link useLocalStorage} | `[value, setValue]` | yes | values you render |
 * | {@link useLocalStorageAccess} | `{ read, write }` | no | effects, handlers, one-shot decisions |
 *
 * ## Hydration
 *
 * The server has no `localStorage`, so {@link useLocalStorage} deliberately
 * yields `initialValue` during SSR and during the hydration render, and only
 * switches to the persisted value once hydration is finished. That costs one
 * extra render but keeps the markup consistent.
 *
 * The catch: effects run against the commit they were scheduled in, so an
 * effect that reads the rendered value on mount still sees `initialValue`,
 * even though storage already holds something else:
 *
 * ```tsx
 * const [cart] = useLocalStorage(KEY, null);
 * useEffect(() => {
 *     load(cart); // BUG on a full page load: `cart` is still null here
 * }, []);
 * ```
 *
 * Reach for {@link useLocalStorageAccess} whenever the persisted value is
 * needed before the post-hydration render lands:
 *
 * ```tsx
 * const cart = useLocalStorageAccess(KEY, null);
 * useEffect(() => {
 *     load(cart.read()); // always the real persisted value
 * }, [cart]);
 * ```
 */
"use client";
import { useState, useCallback, useMemo, useSyncExternalStore } from "react";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";
import { useLogEvent, LogEvent, LogLevel } from "~/hooks/useLogEvent";

const LOG_TAG = "local-storage";

const toReason = (error: unknown): string =>
    error instanceof Error ? error.message : String(error);

// These failures are sticky (corrupted value, blocked storage, exhausted
// quota), so every mount and every storage event would report them again.
// One log per condition is enough to diagnose, the rest is just cost.
const loggedFailures = new Set<string>();

const logFailureOnce = (
    logEvent: LogEvent,
    failure: {
        message: string;
        level: LogLevel;
        error: unknown;
        key?: string;
    }
) => {
    const dedupeKey = `${failure.message}:${failure.key ?? ""}`;
    if (loggedFailures.has(dedupeKey)) return;
    loggedFailures.add(dedupeKey);

    logEvent({
        message: failure.message,
        level: failure.level,
        data: { key: failure.key, reason: toReason(failure.error) },
        tag: LOG_TAG,
    });
};

/**
 * A value that never reached storage is real data loss: it lives in memory
 * until the page is reloaded, and the next load silently falls back.
 */
const logWriteFailure = (logEvent: LogEvent, key: string, error: unknown) =>
    logFailureOnce(logEvent, {
        message: "local_storage_write_failed",
        level: "error",
        error,
        key,
    });

/**
 * `storageArea` is absent on synthetic events, and points at `sessionStorage`
 * for cross-tab session writes we must ignore. Reading `window.localStorage`
 * throws when storage is blocked (private mode / disabled cookies), so mirror
 * the defensive handling in `Storage.ts` instead of killing the listener.
 */
const isLocalStorageEvent = (
    event: StorageEvent,
    logEvent: LogEvent
): boolean => {
    if (!event.storageArea) return true;
    try {
        return event.storageArea === window.localStorage;
    } catch (error) {
        console.warn("Error accessing localStorage:", error);
        logFailureOnce(logEvent, {
            message: "local_storage_access_failed",
            level: "warn",
            error,
        });

        return true;
    }
};

/**
 * `storage` events only fire in *other* tabs, so writes made through this hook
 * have to be broadcast by hand for the rest of this tab to see them.
 */
const subscribersByKey = new Map<string, Set<() => void>>();

const notifySubscribers = (key: string) =>
    subscribersByKey.get(key)?.forEach((onStoreChange) => onStoreChange());

const addSubscriber = (key: string, onStoreChange: () => void) => {
    const subscribers = subscribersByKey.get(key) ?? new Set<() => void>();
    subscribers.add(onStoreChange);
    subscribersByKey.set(key, subscribers);

    return () => {
        subscribers.delete(onStoreChange);
    };
};

/** Cached in place of a parsed value so a corrupted entry is not re-parsed. */
const PARSE_FAILED = Symbol("local-storage-parse-failed");

/**
 * `useSyncExternalStore` re-reads the snapshot on every render and compares it
 * by identity, so a fresh `JSON.parse` result each time would spin forever.
 * Keyed by the raw string, which is what actually changes.
 */
const snapshotCache = new Map<string, { raw: string; value: unknown }>();

const readStoredValue = <T,>(
    key: string,
    fallbackValue: T,
    logEvent: LogEvent
): T => {
    if (typeof window === "undefined") return fallbackValue;

    const raw = getStorageItem(key);
    if (!raw) return fallbackValue;

    const cached = snapshotCache.get(key);
    if (cached?.raw === raw) {
        return cached.value === PARSE_FAILED
            ? fallbackValue
            : (cached.value as T);
    }

    try {
        const value = JSON.parse(raw) as T;
        snapshotCache.set(key, { raw, value });
        return value;
    } catch (error) {
        console.warn(`Error parsing localStorage key "${key}":`, error);
        // Corrupted data means the user silently falls back to defaults
        // (empty cart, missing order snapshot), so this must be visible.
        logFailureOnce(logEvent, {
            message: "local_storage_parse_failed",
            level: "error",
            error,
            key,
        });
        snapshotCache.set(key, { raw, value: PARSE_FAILED });
        return fallbackValue;
    }
};

export type LocalStorageAccess<T> = {
    /**
     * The persisted value, read straight from storage on every call — so it is
     * already correct during the hydration render, unlike the value returned
     * by {@link useLocalStorage}.
     */
    read: () => T;
    /**
     * Persists `value` and notifies every {@link useLocalStorage} watching this
     * key, in this tab and (via the `storage` event) in the others.
     */
    write: (value: T | ((current: T) => T)) => void;
};

/**
 * Imperative access to a stored value. Subscribes to nothing and never
 * re-renders the caller — use it in effects, event handlers, and for one-shot
 * decisions taken on mount.
 *
 * @param key          storage key, from `STORAGE_KEYS`
 * @param initialValue returned when the key is missing or holds invalid JSON
 */
export function useLocalStorageAccess<T>(
    key: string,
    initialValue: T
): LocalStorageAccess<T> {
    const logEvent = useLogEvent();

    // Callers commonly pass an inline literal (e.g. `[]`), which would give
    // `read` a new identity on every render. The fallback is only ever used on
    // a storage miss, so pin the first one for the lifetime of the hook.
    const [fallbackValue] = useState(() => initialValue);

    const read = useCallback(
        (): T => readStoredValue(key, fallbackValue, logEvent),
        [key, fallbackValue, logEvent]
    );

    const write = useCallback(
        (value: T | ((current: T) => T)) => {
            try {
                const valueToStore =
                    value instanceof Function ? value(read()) : value;

                if (typeof window === "undefined") return;

                // `setStorageItem` swallows the browser exception (quota
                // exceeded, blocked storage) and falls back to its in-memory
                // store, which `getStorageItem` still reads — so the value
                // stays readable for this page load either way.
                const { persisted, error } = setStorageItem(
                    key,
                    JSON.stringify(valueToStore)
                );

                if (!persisted) {
                    logWriteFailure(logEvent, key, error);
                }
            } catch (error) {
                console.warn(`Error setting localStorage key "${key}":`, error);
                logWriteFailure(logEvent, key, error);
            } finally {
                notifySubscribers(key);
            }
        },
        [key, read, logEvent]
    );

    return useMemo(() => ({ read, write }), [read, write]);
}

/**
 * Reactive access to a stored value, shaped like `useState`. Re-renders the
 * caller whenever the key changes — through `write`, through another component
 * on the same key, or through another tab.
 *
 * Yields `initialValue` during SSR and hydration; see the module docs above
 * before reading the returned value inside a mount-only effect.
 *
 * @param key          storage key, from `STORAGE_KEYS`
 * @param initialValue returned when the key is missing or holds invalid JSON
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
    const logEvent = useLogEvent();
    const [fallbackValue] = useState(() => initialValue);
    const { read, write } = useLocalStorageAccess(key, fallbackValue);

    // The server has no `localStorage`, so the hydration render has to fall
    // back to `initialValue` to match the markup React is reconciling against.
    const readServerValue = useCallback(() => fallbackValue, [fallbackValue]);

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            const handleStorageChange = (event: StorageEvent) => {
                // A `null` key means the whole storage was cleared, so our
                // value is gone too and has to be re-read.
                const isRelevantKey = event.key === key || event.key === null;

                if (!isRelevantKey || !isLocalStorageEvent(event, logEvent)) {
                    return;
                }

                onStoreChange();
            };

            window.addEventListener("storage", handleStorageChange);
            const removeSubscriber = addSubscriber(key, onStoreChange);

            return () => {
                window.removeEventListener("storage", handleStorageChange);
                removeSubscriber();
            };
        },
        [key, logEvent]
    );

    const storedValue = useSyncExternalStore(subscribe, read, readServerValue);

    return [storedValue, write] as const;
}
